import { toast } from '@repo/design-system/components/ui/toaster';
import { useUpdateMap3dObjectMutation } from '@repo/store/maps/api/mapsApi';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId } from '@repo/store/maps/slices/mapData';
import type { MapData3dObject } from '@repo/types/maps/map-3d-types';
import { useCallback, useState } from 'react';
import { upload3dModelToR2 } from '../../actions/upload-3d-model';
export function useObject3dUpload() {
  const externalId = useAppSelector(selectExternalMapId);
  const [updateMapObjectMutation] = useUpdateMap3dObjectMutation();
  const [isUploadingModel, setIsUploadingModel] = useState(false);
  const token = useAppSelector(state => state.auth.token);
  const uploadModelFile = useCallback(async (objectExternalId: string, modelFile: File, currentModel?: MapData3dObject) => {
    if (!externalId) return;
    setIsUploadingModel(true);
    try {
      const uploadResult = await upload3dModelToR2({
        file: modelFile,
        gameMapExternalId: externalId,
        objectExternalId,
        token
      });
      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Upload failed');
      }
      if (!currentModel) {
        throw new Error('Current model data is required to update properties');
      }
      await updateMapObjectMutation({
        externalId: objectExternalId,
        request: {
          geometry: currentModel.geometry,
          properties: {
            ...currentModel.properties,
            modelPath: uploadResult.cdnPath
          }
        },
        gameMapExternalId: externalId
      }).unwrap();
      toast({
        title: 'Model uploaded successfully',
        variant: 'success'
      });
      return uploadResult.cdnPath;
    } catch (error) {
      toast({
        title: 'Failed to upload model',
        description: error instanceof Error ? error.message : JSON.stringify(error),
        variant: 'error'
      });
      throw error;
    } finally {
      setIsUploadingModel(false);
    }
  }, [externalId, updateMapObjectMutation, token]);
  return {
    uploadModelFile,
    isUploadingModel
  };
}